import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import { Tag } from 'models/tag';
import WCCError from 'models/wccError';
import './picker.html';
import './picker.scss';

components.preload('tag');

export interface TagsPickerVMConfig {
    tags: SubscribableOrNullableValue<Array<Tag>>
    selectedTag?: SubscribableOrNullableValue<Tag>
    multiple?: SubscribableOrNullableValue<boolean>
    hideRoot?: SubscribableOrNullableValue<boolean>
}

interface TagsPickerVMItem {
    tag: Tag,
    isSelected: PureComputed<boolean>
}

@injectable()
export default class TagsPickerVM {
    private allowMultiple: Subscribable<boolean>

    items: Subscribable<Array<TagsPickerVMItem>>
    selectedTag: Observable<Tag | undefined>

    selectedTags = ko.observableArray<Tag>();

    isRootHidden: SubscribableOrNullableValue<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: TagsPickerVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const tags = ko.flattenComputed(config.tags, []);
        this.selectedTag = ko.observable(config.selectedTag).unwrap(false);
        this.allowMultiple = ko.flattenComputed(config.multiple, false);
        this.isRootHidden = config.hideRoot;

        this.items = tags.map(tag => (<TagsPickerVMItem>{
            tag: tag,
            isSelected: this.selectedTags.is(tags => tags.includes(tag))
        }));

        effects.register(tags => {
            const selectedTag = _.last(tags);

            if (this.selectedTag() != selectedTag)
                this.selectedTag(selectedTag);
        }, [this.selectedTags]);

        effects.register(selectedTag => {
            if (selectedTag == undefined && this.selectedTags().length > 0)
                this.clear();
        }, [this.selectedTag]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'tag-picker': true
            }
        });
    }

    select(ids: Array<string>) {
        const tags = this.items().map(i => i.tag).filter(t => ids.includes(<string>t.contentTagId()));

        if (!this.allowMultiple() && tags.length > 1)
            throw new WCCError(messages.InvalidRequest);

        this.selectedTags(tags);
    }

    toggle = ({ tag }: TagsPickerVMItem) => {
        if (this.allowMultiple()) {
            if (this.selectedTags().includes(tag))
                this.selectedTags.remove(tag);
            else
                this.selectedTags.push(tag);
        } else {
            if (this.selectedTag() != tag)
                this.selectedTags([tag]);
        }
    }

    clear = () => {
        this.selectedTags([]);
    }
}